import { useEffect } from 'react';
import type { SelectedFiles } from '@witmetrics/utils';
import { UNISON_PROJECT_TYPE_ID } from '@witmetrics/utils/constants';
import { isNullish } from '@witmetrics/utils/misc';
import { useActiveAccountCreditBalance } from '@witmetrics/data-store';
import type { FormParameters } from '@witmetrics/unison-core';
import { useToggle } from '@/hooks/useToggle';
import { useNumber } from '@/hooks/useNumber';
import { useNewCase } from '@/providers/NewCaseProvider';
import { useAppState } from '@/providers/AppStateProvider';
import { WIZARD_STEPS } from '@/dialogs/NewCaseDialog/constants';
import { DefaultDialogActions } from '@/components/Dialog';
import Button from '@/components/Button';
import type { WizardStep } from '@/dialogs/NewCaseDialog/types';
import TokenCount from './TokenCount';
import { classes } from './utils';

const { SELECT_PATIENT, SELECT_FILES, CASE_SETTINGS, REVIEW } = WIZARD_STEPS;
const { OTHER, VENEERS } = UNISON_PROJECT_TYPE_ID;

export type CaseFooterProps = {
  isSaving: boolean;
  isSkippable: boolean;
  isSaveEnabled: boolean;
  step: WizardStep;
  onFetchTokenCount: (
    params: FormParameters,
    selectedFiles: SelectedFiles,
    currentTokenCount: number
  ) => Promise<number>;
  onNext: (step: WizardStep) => void;
  onSave: () => void;
  onClose: () => void;
};

export default function CaseFooter({
  isSaving,
  isSkippable,
  isSaveEnabled,
  step,
  onFetchTokenCount,
  onNext,
  onSave,
  onClose,
}: CaseFooterProps) {
  const { onApiError } = useAppState();
  const balance = useActiveAccountCreditBalance();
  const { selectedFiles, params, projectTypeId, serviceType } = useNewCase();
  const [tokenCount, setTokenCount] = useNumber(0);
  const [isFetchingTokenCount, toggleFetchingTokenCount] = useToggle();

  const isInsufficient =
    !isFetchingTokenCount && !isNullish(balance) && tokenCount > balance;
  const isConfirmDisabled =
    !isSaveEnabled || (step === REVIEW && isInsufficient);

  useEffect(() => {
    if (serviceType) handleFetchTokenCount();
  }, [params, selectedFiles]);

  const handleFetchTokenCount = async () => {
    try {
      toggleFetchingTokenCount(true);
      setTokenCount(await onFetchTokenCount(params, selectedFiles, tokenCount));
      toggleFetchingTokenCount(false);
    } catch (err) {
      toggleFetchingTokenCount(false);
      onApiError(err, 'Error calculating credits');
    }
  };

  const handleNext = () => {
    switch (step) {
      case SELECT_PATIENT:
        return onNext(SELECT_FILES);
      case SELECT_FILES:
        // NOTE: For "Other" cases, there's no custom settings to set
        return onNext(
          [OTHER, VENEERS].includes(projectTypeId) ? REVIEW : CASE_SETTINGS
        );
      case CASE_SETTINGS:
        return onNext(REVIEW);
      case REVIEW:
        return onSave();
    }
  };

  return (
    <DefaultDialogActions
      disabled={isConfirmDisabled}
      pending={isSaving}
      confirmLabel={step === REVIEW ? 'Save' : 'Next'}
      pendingLabel="Saving"
      adornment={
        <div className={classes.adornment}>
          {serviceType && (
            <TokenCount
              pending={isFetchingTokenCount}
              insufficient={isInsufficient}
              count={tokenCount}
              availableBalance={balance}
            />
          )}
          {isSkippable && (
            <Button variant="ghost" onClick={handleNext}>
              Skip
            </Button>
          )}
        </div>
      }
      onCancel={onClose}
      onConfirm={handleNext}
    />
  );
}
